import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
} from 'react';
import { useSupabaseAuth } from './AuthContext';
import { apiFetch } from '../apiFetch';
import type { Workspace, CreateWorkspaceRequest } from '../../types/database';

const SELECTED_WORKSPACE_KEY = 'selectedWorkspaceId';

export interface WorkspaceContextType {
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  loading: boolean;
  error: string | null;
  fetchWorkspaces: () => Promise<void>;
  setCurrentWorkspace: (workspace: Workspace | null) => void;
  createWorkspace: (data: CreateWorkspaceRequest) => Promise<Workspace>;
  updateWorkspace: (id: string, data: Partial<Workspace>) => Promise<void>;
  deleteWorkspace: (id: string) => Promise<void>;
  /** @deprecated use addCollaborator */
  addMember: (workspaceId: string, userId: string) => Promise<void>;
  /** @deprecated use removeCollaborator */
  removeMember: (workspaceId: string, userId: string) => Promise<void>;
  addCollaborator: (
    workspaceId: string,
    userId: string,
    canEdit?: boolean
  ) => Promise<void>;
  removeCollaborator: (workspaceId: string, userId: string) => Promise<void>;
}

export const WorkspaceContext = createContext<WorkspaceContextType | undefined>(
  undefined
);

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isSignedIn } = useSupabaseAuth();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(
    () => {
      try {
        const saved = localStorage.getItem(SELECTED_WORKSPACE_KEY);
        return saved ? ({ id: saved } as Workspace) : null;
      } catch {
        return null;
      }
    }
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchWorkspaces = useCallback(async () => {
    if (!isSignedIn) {
      setWorkspaces([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      let res: Response;
      try {
        res = await apiFetch('/api/workspaces', {
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeout);
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          (body as { error?: string }).error ??
            `Failed to fetch workspaces: ${res.status}`
        );
      }
      const data: Workspace[] = await res.json();
      setWorkspaces(data || []);
      if (data && data.length > 0) {
        const savedId = localStorage.getItem(SELECTED_WORKSPACE_KEY);
        const saved = savedId ? data.find((w) => w.id === savedId) : null;
        setCurrentWorkspace(saved ?? data[0]);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.name === 'AbortError'
            ? 'Request timed out — check that the API server is running on port 3001'
            : err.message
          : 'Failed to fetch workspaces'
      );
    } finally {
      setLoading(false);
    }
    // currentWorkspace intentionally omitted — only auto-select once on first load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn]);

  const createWorkspace = useCallback(
    async (data: CreateWorkspaceRequest): Promise<Workspace> => {
      const res = await apiFetch('/api/workspaces', {
        method: 'POST',
        body: JSON.stringify({
          name: data.name,
          description: data.description,
          kind: data.kind ?? 'custom',
          jurisdiction_id: data.jurisdiction_id ?? null,
          jurisdiction_ids: data.jurisdiction_ids ?? null,
          theme_id: data.theme_id ?? null,
          org_theme_id: data.org_theme_id ?? null,
          include_descendants: data.include_descendants ?? true,
          date_from: data.date_from ?? null,
          date_to: data.date_to ?? null,
        }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        const body = e as { error?: string; message?: string };
        throw new Error(
          [body.error, body.message].filter(Boolean).join(': ') ||
            'Failed to create workspace'
        );
      }
      const workspace: Workspace = await res.json();
      setWorkspaces((prev) => [workspace, ...prev]);
      return workspace;
    },
    []
  );

  const updateWorkspace = useCallback(
    async (id: string, data: Partial<Workspace>): Promise<void> => {
      const res = await apiFetch(`/api/workspaces/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(
          (e as { error?: string }).error ?? 'Failed to update workspace'
        );
      }
      const updated: Workspace = await res.json();
      setWorkspaces((prev) => prev.map((w) => (w.id === id ? updated : w)));
      if (currentWorkspace?.id === id) setCurrentWorkspace(updated);
    },
    [currentWorkspace]
  );

  const deleteWorkspace = useCallback(
    async (id: string): Promise<void> => {
      const res = await apiFetch(`/api/workspaces/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(
          (e as { error?: string }).error ?? 'Failed to delete workspace'
        );
      }
      setWorkspaces((prev) => prev.filter((w) => w.id !== id));
      if (currentWorkspace?.id === id) {
        setCurrentWorkspace(workspaces.find((w) => w.id !== id) ?? null);
      }
    },
    [currentWorkspace, workspaces]
  );

  const addMember = useCallback(
    async (workspaceId: string, userId: string): Promise<void> => {
      // Delegates to addCollaborator for backward compatibility
      const res = await apiFetch(
        `/api/workspaces/${workspaceId}/collaborators`,
        {
          method: 'POST',
          body: JSON.stringify({ user_id: userId, can_edit: false }),
        }
      );
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(
          (e as { error?: string }).error ?? 'Failed to add member'
        );
      }
    },
    []
  );

  const removeMember = useCallback(
    async (workspaceId: string, userId: string): Promise<void> => {
      const res = await apiFetch(
        `/api/workspaces/${workspaceId}/collaborators/${userId}`,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(
          (e as { error?: string }).error ?? 'Failed to remove member'
        );
      }
    },
    []
  );

  const addCollaborator = useCallback(
    async (
      workspaceId: string,
      userId: string,
      canEdit = false
    ): Promise<void> => {
      const res = await apiFetch(
        `/api/workspaces/${workspaceId}/collaborators`,
        {
          method: 'POST',
          body: JSON.stringify({ user_id: userId, can_edit: canEdit }),
        }
      );
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(
          (e as { error?: string }).error ?? 'Failed to add collaborator'
        );
      }
    },
    []
  );

  const removeCollaborator = useCallback(
    async (workspaceId: string, userId: string): Promise<void> => {
      const res = await apiFetch(
        `/api/workspaces/${workspaceId}/collaborators/${userId}`,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(
          (e as { error?: string }).error ?? 'Failed to remove collaborator'
        );
      }
    },
    []
  );

  useEffect(() => {
    if (isSignedIn) {
      void fetchWorkspaces();
    } else {
      setWorkspaces([]);
      setCurrentWorkspace(null);
      setLoading(false);
    }
  }, [isSignedIn, fetchWorkspaces]);

  // Persist selected workspace across page reloads
  useEffect(() => {
    if (currentWorkspace?.id) {
      try {
        localStorage.setItem(SELECTED_WORKSPACE_KEY, currentWorkspace.id);
      } catch { /* non-critical */ }
    }
  }, [currentWorkspace?.id]);

  const value: WorkspaceContextType = {
    workspaces,
    currentWorkspace,
    loading,
    error,
    fetchWorkspaces,
    setCurrentWorkspace,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    addMember,
    removeMember,
    addCollaborator,
    removeCollaborator,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspaces = () => {
  const context = useContext(WorkspaceContext);
  if (!context)
    throw new Error('useWorkspaces must be used within WorkspaceProvider');
  return context;
};
